/**
 * Касата на същия компютър — през `127.0.0.1` (docs/edge.md §Касата на
 * същия компютър).
 *
 * Обект с ЕДНА машина (каса и терминал в едно) няма нужда от LAN адреса, а
 * той е и най-чупливото звено: доставчик, който раздава ПУБЛИЧЕН адрес
 * направо на машината (браузърът спира HTTP към публичен IP като mixed
 * content, дори да е собственият), смяна на IP-то след рестарт на рутера,
 * firewall. Loopback не зависи от нито едно от тях, а Chrome не го смята за
 * mixed content. Затова сондата (`edgeSession.ts`) проверява и него — и
 * ако там отговори ГЛАВНАТА каса (същият `client_id` като `masterClientId`
 * от облака), устройството го запомня (`edge_this_till`) и оттам нататък
 * `getEdgeBaseUrl()` води към `127.0.0.1`.
 *
 * Кога се пробва (`mayLookForTillOnThisDevice`) е нарочно тясно: устройство
 * в залата, което стига до касата по LAN, никога не стига дотук — то работи
 * точно както преди.
 */
import axios from "axios";
import { getEdgeHealth, type EdgeHealthOut } from "../../api/edge";
import { devLog } from "./edgeRuntime";
import {
  getEdgeConfigState,
  getEdgeLoopbackBaseUrl,
  isPrivateLanAddress,
  setThisTillClientId,
} from "./edgeState";

/** Телефон или таблет не е касата (FiscalAPI е Windows услуга) и не бива да
 * получава въпрос от браузъра за „приложенията на устройството" заради нея. */
function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const hints = (navigator as Navigator & { userAgentData?: { mobile?: boolean } })
    .userAgentData;
  if (hints?.mobile) return true;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

/**
 * Да провери ли сондата и касата на ТОЗИ компютър:
 *  • обявеният адрес не е частен (публичен от доставчика) — по него браузърът
 *    така или иначе не стига, а на самата каса loopback е единственият път;
 *    пробва се и без човек, иначе обектът с една машина не тръгва сам;
 *  • иначе само при изрично действие („Подготви", „Провери") — LAN пътят е
 *    паднал и човекът търси защо. Докосването след пропаднала сонда
 *    (`edgePermission.ts`) НЕ е такова: то идва на всеки терминал в залата,
 *    щом касата е изгасена, а въпрос на браузъра за „приложенията на
 *    устройството" там щеше да е нов и безсмислен.
 * И в двата случая — само ако облакът е казал коя е главната каса: без
 * `masterClientId` няма с какво да се сравни отговорилата.
 */
export function mayLookForTillOnThisDevice(humanAction: boolean): boolean {
  const config = getEdgeConfigState();
  if (!config?.enabled || !config.masterClientId) return false;
  if (isMobileDevice()) return false;
  if (config.ip && !isPrivateLanAddress(config.ip)) return true;
  return humanAction;
}

/**
 * `GET http://127.0.0.1:{port}/edge/health`. Отговорът се приема САМО ако е
 * от главната каса — друга инсталация на FiscalAPI на същия компютър (втора
 * каса без резерв, тестова) не прави устройството „касата". Самоличността се
 * запомня и при 503 (касата е там, но още без snapshot): достъпността е
 * отделен въпрос, който сондата решава по `ok`. Връща отговора само при 2xx —
 * 503 е „не стига" за сондата, както и по LAN.
 */
export async function probeTillOnThisDevice(): Promise<EdgeHealthOut | null> {
  const base = getEdgeLoopbackBaseUrl();
  const master = getEdgeConfigState()?.masterClientId;
  if (!base || !master) return null;
  try {
    const health = await getEdgeHealth(base);
    if (health.client_id !== master) return null;
    setThisTillClientId(health.client_id);
    return health;
  } catch (error) {
    const body = axios.isAxiosError<EdgeHealthOut>(error)
      ? error.response?.data
      : undefined;
    if (body?.client_id && body.client_id === master) {
      setThisTillClientId(body.client_id);
    }
    devLog("till probe on this device failed", error);
    return null;
  }
}

/** Главната каса отговори по LAN, а през `127.0.0.1` — не: запомненото вече
 * не важи (касата е преместена на друга машина или loopback пътят е спрян). */
export function forgetTillOnThisDevice() {
  setThisTillClientId(null);
}
